import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useState } from "react";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Code,
  Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight,
  Undo, Redo, Eraser,
} from "lucide-react";
import { uploadMedia } from "@/lib/adminCmsApi";
import { toast } from "sonner";

function Btn({ active, onClick, title, children, disabled }) {
  return (
    <button type="button" onClick={onClick} title={title} disabled={disabled}
            className={`px-2.5 py-1.5 transition-colors flex items-center justify-center ${
              active ? "bg-[#0E2A57] text-white" : "text-[#0E2A57] hover:bg-ags-cream"
            } disabled:opacity-40`}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder = "Write…", minHeight = 220 }) {
  const [busy, setBusy] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[#0E2A57] underline decoration-[#2BB6E5] decoration-2 underline-offset-2" } }),
      Image,
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm md:prose max-w-none focus:outline-none px-4 py-4",
      },
    },
  });

  // Sync external value changes (e.g. switching records)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = async () => {
    const choice = window.prompt("Image URL — or leave blank to upload from device", "");
    if (choice === null) return;
    if (choice.trim()) {
      editor.chain().focus().setImage({ src: choice.trim() }).run();
      return;
    }
    // file picker
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBusy(true);
      try {
        const res = await uploadMedia(file, { folder: "ags/rte" });
        editor.chain().focus().setImage({ src: res.url, alt: res.alt_text || "" }).run();
      } catch (err) { toast.error("Image upload failed"); }
      finally { setBusy(false); }
    };
    input.click();
  };

  return (
    <div className="border border-[#0E2A57]/15 bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-px bg-[#E5EBF2] border-b border-ags-hairline">
        <div className="bg-white flex">
          <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold size={14} /></Btn>
          <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic size={14} /></Btn>
        </div>
        <div className="bg-white flex">
          <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 size={14} /></Btn>
          <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 size={14} /></Btn>
        </div>
        <div className="bg-white flex">
          <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bulleted list"><List size={14} /></Btn>
          <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered size={14} /></Btn>
          <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><Quote size={14} /></Btn>
          <Btn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block"><Code size={14} /></Btn>
        </div>
        <div className="bg-white flex">
          <Btn active={editor.isActive("link")} onClick={addLink} title="Link"><LinkIcon size={14} /></Btn>
          <Btn onClick={addImage} title="Image" disabled={busy}><ImageIcon size={14} /></Btn>
        </div>
        <div className="bg-white flex">
          <Btn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Align left"><AlignLeft size={14} /></Btn>
          <Btn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Align center"><AlignCenter size={14} /></Btn>
          <Btn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Align right"><AlignRight size={14} /></Btn>
        </div>
        <div className="bg-white flex ml-auto">
          <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting"><Eraser size={14} /></Btn>
          <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><Undo size={14} /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><Redo size={14} /></Btn>
        </div>
      </div>
      <div style={{ minHeight }} className="bg-white">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
